import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminNavBar from "../components/AdminNavBar";
import { AdminPageBody } from "../components/AdminPageShell";
import { useAdminLearningPaths } from "../store/admin";
import StaffPagination from "./components/staff/StaffPagination";
import { Route, RefreshCw, Eye } from "lucide-react";

/** Collapse the API status values into the three UI buckets. */
function mapStatus(raw) {
  const s = String(raw || "PENDING").toUpperCase();
  if (s === "DRAFT") return "Draft";
  if (["PENDING", "PENDING_REVIEW", "UNDER_REVIEW"].includes(s)) return "Pending";
  if (["REJECTED", "DISABLED", "ARCHIVED", "SUSPENDED", "WITHDRAWN"].includes(s)) return "Archived";
  if (["APPROVED", "ACTIVE", "PUBLISHED"].includes(s)) return "Active";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Pending: "bg-[#dae2fd] text-[#565e74]",
  Draft: "bg-slate-100 text-slate-600",
  Archived: "bg-[#e0e3e5] text-[#434655]",
};

const formatMoney = (value) =>
  value == null
    ? "Free"
    : `₦${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** Count the courses bundled into a path across the shapes the API may use. */
function courseCount(lp) {
  const list = lp.courses || lp.items || lp.pathCourses || [];
  return Array.isArray(list) ? list.length : lp.courseCount ?? 0;
}

/**
 * Map a `/api/admin/learning-paths` record into the row shape rendered below.
 * The creator is a nested object (`createdBy: { id, name, email }`), so always
 * resolve it to a string — never let an object reach the DOM.
 */
function mapLearningPath(raw) {
  const updated = raw.updatedAt || raw.createdAt;
  const author =
    raw.createdBy?.name ||
    raw.creatorName ||
    raw.author ||
    raw.instructorName ||
    raw.uploaderName ||
    "—";
  return {
    id: raw.id,
    title: raw.title || raw.name || "Untitled path",
    description: raw.description || "",
    author,
    status: mapStatus(raw.status),
    rawStatus: String(raw.status || "PENDING").toUpperCase(),
    courses: courseCount(raw),
    price: formatMoney(raw.price),
    updated: updated ? new Date(updated).toLocaleDateString() : "—",
  };
}

const LearningPaths = () => {
  const navigate = useNavigate();
  const {
    learningPaths,
    learningPathsLoading,
    learningPathsError,
    learningPathsTotal,
    learningPathsPages,
    fetchLearningPaths,
  } = useAdminLearningPaths();

  // Status filter is applied server-side via the optional `status` query param.
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  // The list envelope is `{ items, total, page, limit, totalPages }` — the
  // backend paginates, so `page` must be sent on every fetch.
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    fetchLearningPaths({
      page,
      limit,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      sort,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, sort, page, limit]);

  // Changing the filter invalidates the current page, so restart at page 1.
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const rows = useMemo(() => (learningPaths || []).map(mapLearningPath), [learningPaths]);

  const pendingCount = rows.filter((r) => r.status === "Pending").length;

  const errorMessage = learningPathsError;

  // Moderation decisions now happen on a dedicated detail page so the admin
  // can review the full learning path (incl. bundled courses) first.
  const openDetail = (row) => navigate(`/admin/learning-paths/${row.id}`);

  return (
    <div>
      <AdminNavBar
        title="Learning Paths"
        subTitle="Moderation queue"
        context="Review learning paths submitted by resource persons before they are published to learners."
      />
      <AdminPageBody>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-[28px] leading-9 font-bold text-[#191c1e] mb-1">
              Learning Path Moderation
            </h1>
            <p className="text-sm text-[#434655]">
              Review learning paths submitted by resource persons. A path bundles approved courses
              and must be approved before learners can see it.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search learning paths"
              className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
            />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="DISABLED">Disabled</option>
            </select>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="price_asc">Price (low to high)</option>
              <option value="price_desc">Price (high to low)</option>
            </select>
            <button
              onClick={() =>
                fetchLearningPaths({
                  page,
                  limit,
                  ...(statusFilter ? { status: statusFilter } : {}),
                  ...(search.trim() ? { search: search.trim() } : {}),
                  sort,
                })
              }
              disabled={learningPathsLoading}
              className="flex items-center gap-2 bg-white border border-[#c3c6d7] rounded-lg text-sm px-4 py-2 text-[#191c1e] hover:bg-[#f2f4f6] transition-colors disabled:opacity-60"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-[#c3c6d7] shadow-sm">
            <p className="text-[12px] leading-4 tracking-wider text-[#434655] uppercase font-semibold">
              Total Paths
            </p>
            <p className="text-[28px] leading-9 font-bold text-[#191c1e] mt-2">
              {(learningPathsTotal || rows.length).toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-[#c3c6d7] shadow-sm">
            <p className="text-[12px] leading-4 tracking-wider text-[#434655] uppercase font-semibold">
              Awaiting Review
            </p>
            <p className="text-[28px] leading-9 font-bold text-[#004ac6] mt-2">{pendingCount}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-[#c3c6d7] shadow-sm">
            <p className="text-[12px] leading-4 tracking-wider text-[#434655] uppercase font-semibold">
              Published
            </p>
            <p className="text-[28px] leading-9 font-bold text-green-700 mt-2">
              {rows.filter((r) => r.status === "Active").length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#c3c6d7] shadow-sm">
          <div className="p-6 border-b border-[#c3c6d7]">
            <h5 className="text-[20px] leading-7 font-semibold text-[#191c1e]">
              Submitted Learning Paths
            </h5>
          </div>

          {learningPathsLoading && (
            <div className="px-6 py-3 text-sm text-[#434655] border-b border-[#c3c6d7]">
              Loading learning paths…
            </div>
          )}
          {errorMessage && !learningPathsLoading && (
            <div className="px-6 py-3 text-sm text-[#ba1a1a] border-b border-[#c3c6d7]">
              {errorMessage}
            </div>
          )}

          {rows.length === 0 && !learningPathsLoading ? (
            <div className="px-6 py-16 text-center text-sm text-[#737686]">
              No learning paths found{statusFilter ? ` with status ${statusFilter}` : ""}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#f2f4f6] text-[12px] leading-4 tracking-wider text-[#434655] uppercase">
                    <th className="px-6 py-4 font-semibold">Learning Path</th>
                    <th className="px-6 py-4 font-semibold">Author</th>
                    <th className="px-6 py-4 font-semibold text-center">Courses</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold text-right">Price</th>
                    <th className="px-6 py-4 font-semibold text-right">Updated</th>
                    <th className="px-6 py-4 font-semibold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-[#191c1e]">
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-[#e6e8ea] hover:bg-[#fafbfc]">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#dae2fd] text-[#131b2e] flex items-center justify-center shrink-0">
                            <Route className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold truncate">{r.title}</p>
                            <p className="text-[#737686] text-xs truncate max-w-[320px]">
                              {r.description || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{r.author}</td>
                      <td className="px-6 py-4 text-center">{r.courses}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${statusStyles[r.status] || statusStyles.Pending}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-['JetBrains_Mono',monospace] text-[13px]">
                        {r.price}
                      </td>
                      <td className="px-6 py-4 text-right text-[#434655]">{r.updated}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openDetail(r)}
                          title="Review details before deciding"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#dae2fd] text-[#004ac6] hover:bg-[#c9d6ff] transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" /> Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="p-4 border-t border-[#c3c6d7] flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#434655]">
              Showing {rows.length} learning path{rows.length === 1 ? "" : "s"}
              {learningPathsTotal != null && learningPathsTotal > 0
                ? ` of ${learningPathsTotal}`
                : ""}
              {" "}(page {page} of {learningPathsPages || 1})
            </p>
            <StaffPagination
              page={page}
              lastPage={learningPathsPages || 1}
              onPageChange={setPage}
            />
          </div>
        </div>
      </AdminPageBody>
    </div>
  );
};

export default LearningPaths;
